import { DynamicWidget, useDynamicContext } from '@dynamic-labs/sdk-react-core';
import { CheckIcon } from 'lucide-react';
import React, { ReactNode, useMemo } from 'react';

import { HomeButton } from '@/components/buttons/HomeButton';
import ThemeSwitcher from '@/components/ThemeSwitcher';
import { Text } from '@/design-system/components/ui';

const MAX_WIDTH = 1024;
const TOP_BAR_HEIGHT = 56;
const HEADER_HEIGHT = 140;
const BOTTOM_BAR_HEIGHT = 120;

interface StepConfig {
  label: string;
}

interface Props {
  children: ReactNode;
  title: string;
  steps: StepConfig[];
  activeStep: number;
  actions?: ReactNode;
}

const StepIndicator = ({
  active,
  completed,
  stepNumber,
}: {
  active: boolean;
  completed: boolean;
  stepNumber: number;
}) => (
  <div
    className={`tw:flex tw:items-center tw:justify-center tw:w-10 tw:h-10 tw:rounded-full tw:shrink-0 tw:text-base tw:font-medium ${
      active || completed ? 'tw:bg-fuul-blue tw:text-white' : 'tw:bg-[#D2D6DB] tw:text-black'
    }`}
  >
    {completed ? <CheckIcon size={20} /> : stepNumber}
  </div>
);

const StepConnector = ({ completed }: { completed: boolean }) => (
  <div className="tw:h-px tw:flex-1" style={{ backgroundColor: completed ? '#6366F1' : '#BABBBB' }} />
);

export const FullPageStepperFormTemplate = ({ children, title, steps, activeStep, actions }: Props) => {
  const { primaryWallet } = useDynamicContext();
  const isAuthenticated = useMemo(() => !!primaryWallet, [primaryWallet]);

  return (
    <div className="tw:flex tw:flex-col tw:min-h-screen tw:bg-background">
      {/* Fixed Top Bar with Logo and Wallet */}
      <header className="tw:fixed tw:top-0 tw:left-0 tw:right-0 tw:w-full tw:z-[1100] tw:backdrop-blur-[10px] tw:bg-background/80">
        <nav className="tw:flex tw:flex-row tw:items-center tw:justify-between tw:p-2 tw:px-6">
          <HomeButton />
          <div className="tw:flex tw:flex-row tw:gap-4 tw:items-center">
            <ThemeSwitcher />
            {isAuthenticated && <DynamicWidget />}
          </div>
        </nav>
      </header>

      {/* Fixed Header with Title and Stepper */}
      <div
        className="tw:fixed tw:left-0 tw:right-0 tw:z-[1099] tw:bg-background tw:flex tw:justify-center tw:pt-8 tw:pb-4"
        style={{ top: TOP_BAR_HEIGHT }}
      >
        <div className="tw:w-full tw:px-4 tw:flex tw:flex-col tw:gap-6" style={{ maxWidth: MAX_WIDTH }}>
          <Text variant="display-sm" as="h3" className="tw:m-0">
            {title}
          </Text>

          {/* Stepper */}
          <div className="tw:flex tw:flex-row tw:items-center tw:w-full">
            {steps.map((step, index) => {
              const isActive = index === activeStep;
              const isCompleted = index < activeStep;
              return (
                <React.Fragment key={step.label}>
                  <div className="tw:flex tw:flex-row tw:items-center tw:shrink-0">
                    <StepIndicator active={isActive} completed={isCompleted} stepNumber={index + 1} />
                    {isActive && (
                      <span className="tw:text-xs tw:font-semibold tw:text-fuul-blue tw:pl-2 tw:whitespace-nowrap tw:uppercase">
                        {step.label}
                      </span>
                    )}
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className="tw:flex tw:items-center tw:flex-1 tw:h-10"
                      style={{
                        paddingLeft: isActive ? 16 : 0,
                        paddingRight: index + 1 === activeStep ? 16 : 0,
                      }}
                    >
                      <StepConnector completed={isCompleted} />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <main
        className="tw:flex tw:justify-center tw:items-start tw:flex-1"
        style={{
          paddingTop: `${TOP_BAR_HEIGHT + HEADER_HEIGHT}px`,
          paddingBottom: `${BOTTOM_BAR_HEIGHT}px`,
        }}
      >
        <div className="tw:w-full tw:px-4 tw:py-6" style={{ maxWidth: MAX_WIDTH }}>
          {children}
        </div>
      </main>

      {/* Fixed Bottom Bar for Actions */}
      {actions && (
        <div className="tw:fixed tw:bottom-0 tw:left-0 tw:right-0 tw:flex tw:justify-center tw:pb-6 tw:pt-4 tw:bg-background tw:z-[1100] tw:border-t tw:border-border">
          <div
            className="tw:flex tw:flex-row tw:justify-between tw:items-center tw:w-full tw:px-4"
            style={{ maxWidth: MAX_WIDTH }}
          >
            {actions}
          </div>
        </div>
      )}
    </div>
  );
};
